import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  MenuItem,
  Grid,
  InputAdornment,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Assessment as ReportsIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useCashOutV2 } from "@/hooks/useCashOutV2";
import { usePermissions } from "@/hooks/usePermissions";
import { CashOutList } from "@/components/cashOut/CashOutList";
import { ErrorDisplay } from "@/components/common/ErrorDisplay";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";
import { toast } from "react-hot-toast";
import { VENUES, type Venue } from "@restaurant-staff-portal/shared";
import { trpc } from "@/lib/api/trpc";

export function CashOutListPage() {
  const navigate = useNavigate();
  const { cashOutPermissions } = usePermissions();
  const { approveCashOut, exportCashOuts } = useCashOutV2();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [filters, setFilters] = useState({
    venue: "" as Venue | "",
    startDate: null as Date | null,
    endDate: null as Date | null,
    userId: "",
  });

  // Use appropriate query based on permissions
  // @ts-ignore - tRPC type issues
  const listAllQuery = trpc.cashOutV2.listAll.useQuery(
    {
      venue: filters.venue || undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      userId: filters.userId || undefined,
      page,
      limit,
    },
    { enabled: cashOutPermissions.canViewAll }
  );

  // @ts-ignore - tRPC type issues
  const myQuery = trpc.cashOutV2.getMy.useQuery(
    { page, limit },
    { enabled: !cashOutPermissions.canViewAll }
  );

  const cashOutsQuery = cashOutPermissions.canViewAll ? listAllQuery : myQuery;

  const { data, error, isLoading } = cashOutsQuery;

  const handleApprove = async (id: string) => {
    try {
      await approveCashOut({ id, approved: true });
      // The refetch is now handled by query invalidation in the mutation's onSuccess
      // No need to manually refetch here
    } catch (error) {
      // Error handled by hook
    }
  };

  const handleExport = async () => {
    if (!filters.startDate || !filters.endDate) {
      toast.error("Please select start and end dates for export");
      return;
    }

    try {
      await exportCashOuts({
        venue: filters.venue || undefined,
        userId: filters.userId || undefined,
        startDate: filters.startDate,
        endDate: filters.endDate,
        format: "csv",
      });
    } catch (error) {
      // Error handled by hook
    }
  };

  if (error) {
    return <ErrorDisplay error={error} />;
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h4">Cash Outs</Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            {cashOutPermissions.canExportCSV && (
              <Button
                variant="outlined"
                startIcon={<ReportsIcon />}
                onClick={() => navigate("/cash-outs/reports")}
              >
                Reports
              </Button>
            )}
            {cashOutPermissions.canCreate && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate("/cash-outs/create")}
              >
                New Cash Out
              </Button>
            )}
          </Box>
        </Box>

        {/* Filters */}
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Venue"
                value={filters.venue}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    venue: e.target.value as Venue | "",
                  })
                }
              >
                <MenuItem value="">All Venues</MenuItem>
                {Object.entries(VENUES).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Start Date"
                value={filters.startDate}
                onChange={(date) => setFilters({ ...filters, startDate: date })}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="End Date"
                value={filters.endDate}
                onChange={(date) => setFilters({ ...filters, endDate: date })}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </Grid>

            {cashOutPermissions.canViewAll && (
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Search by Server"
                  value={filters.userId}
                  onChange={(e) =>
                    setFilters({ ...filters, userId: e.target.value })
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            )}
          </Grid>
        </LocalizationProvider>
      </Box>

      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <CashOutList
          cashOuts={data?.cashOuts || []}
          totalCount={data?.pagination?.total || 0}
          page={page}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={setLimit}
          onView={(id) => navigate(`/cash-outs/${id}`)}
          onEdit={(id) => navigate(`/cash-outs/${id}/edit`)}
          onApprove={handleApprove}
          onExport={handleExport}
          venue={filters.venue || undefined}
        />
      )}
    </Container>
  );
}
